import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertCircle,
  Building2,
  CalendarDays,
  ChevronRight,
  Cpu,
  DollarSign,
  Handshake,
  Info,
  Loader2,
  Medal,
  Moon,
  Sun,
  Trophy,
  TrendingUp,
  UserCheck,
  Users,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import websocketService from '../services/websocket';
import useAdminTheme from '../hooks/useAdminTheme';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';

const pad2 = (value) => String(value).padStart(2, '0');

const toMonthValue = (date = new Date()) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

const toDateValue = (date = new Date()) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const formatDashboardRevenue = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return 'Rp 0';
  if (Math.abs(amount) >= 1000000) return `Rp ${(amount / 1000000).toFixed(1)}M`;
  return `Rp ${amount.toLocaleString('id-ID', { maximumFractionDigits: 2 })}`;
};

const toQuarterValue = (date = new Date()) => `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;

const parseMonthValue = (value, fallbackDate = new Date()) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return { year: fallbackDate.getFullYear(), monthIndex: fallbackDate.getMonth() };
  }

  return {
    year: Number(match[1]),
    monthIndex: Number(match[2]) - 1
  };
};

const parseQuarterValue = (value, fallbackDate = new Date()) => {
  const match = String(value || '').match(/^(\d{4})-Q([1-4])$/);
  if (!match) {
    return {
      year: fallbackDate.getFullYear(),
      quarter: Math.floor(fallbackDate.getMonth() / 3) + 1
    };
  }

  return {
    year: Number(match[1]),
    quarter: Number(match[2])
  };
};

const getMonthBounds = (value, now = new Date()) => {
  const { year, monthIndex } = parseMonthValue(value, now);
  const isCurrentMonth = year === now.getFullYear() && monthIndex === now.getMonth();

  return {
    startDate: toDateValue(new Date(year, monthIndex, 1)),
    endDate: toDateValue(isCurrentMonth ? now : new Date(year, monthIndex + 1, 0))
  };
};

const getQuarterBounds = (value, now = new Date()) => {
  const { year, quarter } = parseQuarterValue(value, now);
  const startMonth = (quarter - 1) * 3;
  const isCurrentQuarter = year === now.getFullYear() && quarter === Math.floor(now.getMonth() / 3) + 1;

  return {
    startDate: toDateValue(new Date(year, startMonth, 1)),
    endDate: toDateValue(isCurrentQuarter ? now : new Date(year, startMonth + 3, 0))
  };
};

const getYearBounds = (value, now = new Date()) => {
  const year = Number(value) || now.getFullYear();
  const isCurrentYear = year === now.getFullYear();

  return {
    startDate: toDateValue(new Date(year, 0, 1)),
    endDate: toDateValue(isCurrentYear ? now : new Date(year, 11, 31))
  };
};

const getQuarterOptions = () => {
  const currentYear = new Date().getFullYear();
  return [0, 1].flatMap((yearOffset) => {
    const year = currentYear - yearOffset;
    return [1, 2, 3, 4].map((quarter) => ({
      value: `${year}-Q${quarter}`,
      label: `${year} Q${quarter}`
    }));
  });
};

const PERIOD_OPTIONS = [
  { key: 'today', labelKey: 'dashboard.periodOptions.today', fallback: 'Today' },
  { key: 'month', labelKey: 'dashboard.periodOptions.month', fallback: 'Month' },
  { key: 'quarter', labelKey: 'dashboard.periodOptions.quarter', fallback: 'Quarter' },
  { key: 'year', labelKey: 'dashboard.periodOptions.year', fallback: 'Year' },
  { key: 'custom', labelKey: 'dashboard.periodOptions.custom', fallback: 'Custom' }
];

const getDashboardParams = ({ period, month, quarter, year, customStart, customEnd }) => {
  const today = toDateValue();
  const params = {
    period,
    timezone: 'Asia/Shanghai'
  };

  if (period === 'today') {
    params.startDate = today;
    params.endDate = today;
  }

  if (period === 'month') {
    params.month = month;
    Object.assign(params, getMonthBounds(month));
  }

  if (period === 'quarter') {
    params.quarter = quarter;
    Object.assign(params, getQuarterBounds(quarter));
  }

  if (period === 'year') {
    params.year = year;
    Object.assign(params, getYearBounds(year));
  }

  if (period === 'custom') {
    params.startDate = customStart;
    params.endDate = customEnd;
  }

  return params;
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useAdminTheme();
  const currentAdmin = useAuthStore((state) => state.user);
  const canViewUnits = hasAdminPermission(currentAdmin, 'view_units');
  const canViewUsers = hasAdminPermission(currentAdmin, 'view_users_roles');
  const canViewDashboard = hasAdminPermission(currentAdmin, 'view_dashboard');
  const [stats, setStats] = useState({
    totalUnits: 0,
    activeUnits: 0,
    totalUsers: 0,
    activeUsers: 0,
    rpCount: 0,
    totalRevenue: 0,
    growthRate: 0,
    definitions: {}
  });
  const [rankings, setRankings] = useState({
    puskud: [],
    kud: [],
    steward: []
  });
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState(toMonthValue());
  const [quarter, setQuarter] = useState(toQuarterValue());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [customStart, setCustomStart] = useState(toDateValue(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [customEnd, setCustomEnd] = useState(toDateValue());
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [openDefinition, setOpenDefinition] = useState(null);

  const dashboardParams = useMemo(() => getDashboardParams({
    period,
    month,
    quarter,
    year,
    customStart,
    customEnd
  }), [period, month, quarter, year, customStart, customEnd]);

  const translate = useCallback((key, fallback, options = {}) => (
    t(key, { defaultValue: fallback, ...options })
  ), [t]);

  const periodCaption = translate('dashboard.periodLabel', 'Period').replace(/[:\uFF1A]\s*$/, '');
  const periodLabel = useMemo(() => {
    if (period === 'today') {
      return translate('dashboard.periodOptions.today', 'Today');
    }

    if (period === 'quarter') {
      return quarter ? quarter.replace('-Q', ' Q') : translate('dashboard.periodOptions.quarter', 'Quarter');
    }

    if (period === 'year') {
      return year || translate('dashboard.periodOptions.year', 'Year');
    }

    if (period === 'custom') {
      return customStart && customEnd
        ? `${customStart} to ${customEnd}`
        : translate('dashboard.periodOptions.custom', 'Custom');
    }

    return month || translate('dashboard.periodThisMonth', 'This month');
  }, [customEnd, customStart, month, period, quarter, translate, year]);

  const loadDashboardData = useCallback(async ({ initial = false, silent = false } = {}) => {
    if (initial && !hasLoaded) {
      setLoading(true);
    } else if (!silent) {
      setRefreshing(true);
    }

    setError(null);

    try {
      const [statsResult, rankingsResult] = await Promise.all([
        api.get('/api/dashboard/stats', { params: dashboardParams })
          .then((response) => ({ ok: true, response }))
          .catch((requestError) => ({ ok: false, error: requestError })),
        canViewDashboard
          ? api.get('/api/dashboard/performance-rankings', { params: { ...dashboardParams, limit: 10 } })
            .then((response) => ({ ok: true, response }))
            .catch((requestError) => ({ ok: false, error: requestError }))
          : Promise.resolve(null)
      ]);

      if (statsResult.ok && statsResult.response.data.success) {
        setStats(statsResult.response.data.data);
      } else {
        console.error('Failed to load dashboard stats:', statsResult.error || statsResult.response?.data);
        setError(translate('dashboard.loadError', 'Failed to load dashboard data.'));
      }

      if (canViewDashboard && rankingsResult?.ok && rankingsResult.response.data.success) {
        setRankings({
          puskud: rankingsResult.response.data.data?.puskud || [],
          kud: rankingsResult.response.data.data?.kud || [],
          steward: rankingsResult.response.data.data?.steward || []
        });
      } else {
        if (canViewDashboard) {
          console.warn('Dashboard rankings unavailable:', rankingsResult?.error || rankingsResult?.response?.data);
        }
        setRankings({
          puskud: [],
          kud: [],
          steward: []
        });
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(translate('dashboard.loadError', 'Failed to load dashboard data.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
      setHasLoaded(true);
    }
  }, [canViewDashboard, dashboardParams, hasLoaded, translate]);

  useEffect(() => {
    loadDashboardData({ initial: true });
  }, [loadDashboardData]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadDashboardData({ silent: true });
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [loadDashboardData]);

  useEffect(() => {
    let debounceTimer;

    const handleLiveUpdate = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        loadDashboardData({ silent: true });
      }, 2000);
    };

    websocketService.on('device_update', handleLiveUpdate);
    websocketService.on('device_presence_changed', handleLiveUpdate);
    websocketService.on('transaction_update', handleLiveUpdate);
    websocketService.on('connected', handleLiveUpdate);

    return () => {
      window.clearTimeout(debounceTimer);
      websocketService.off('device_update', handleLiveUpdate);
      websocketService.off('device_presence_changed', handleLiveUpdate);
      websocketService.off('transaction_update', handleLiveUpdate);
      websocketService.off('connected', handleLiveUpdate);
    };
  }, [loadDashboardData]);

  useEffect(() => {
    if (!openDefinition) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (
        event.target.closest('[data-definition-trigger]')
        || event.target.closest('[data-definition-popover]')
      ) {
        return;
      }
      setOpenDefinition(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenDefinition(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDefinition]);

  const activeUserDefinition = stats.definitions?.activeUsers || translate('dashboard.definitions.activeUsers', 'Users with completed water purchase or top-up transactions in the selected period.');
  const revenueDefinition = stats.definitions?.totalRevenue || translate('dashboard.definitions.totalRevenue', 'Completed water purchase revenue in the selected period. Wallet prepayments are not counted here.');
  const stationDefinition = stats.definitions?.totalUnits || translate('dashboard.definitions.totalUnits', 'Current real-time station count. New-station trends are calculated from station creation time in the selected period.');
  const formatSignedPercent = (value) => {
    const rate = Number(value || 0);
    return `${rate > 0 ? '+' : ''}${rate}%`;
  };

  const kpis = [
    {
      key: 'totalUnits',
      title: translate('dashboard.kpi.totalUnits', 'Total stations'),
      value: stats.totalUnits,
      unit: translate('dashboard.kpi.units', 'units'),
      icon: Cpu,
      trend: Number.isFinite(Number(stats.newStations))
        ? translate('dashboard.kpi.newStations', 'New in period: {{count}}', { count: Number(stats.newStations || 0) })
        : translate('dashboard.kpi.stationRealtime', 'Current total'),
      trendPositive: true,
      color: 'border-cyan-400',
      definition: stationDefinition
    },
    {
      key: 'activeUsers',
      title: translate('dashboard.kpi.activeUsers', 'Active users'),
      value: stats.activeUsers ?? stats.totalUsers,
      unit: translate('dashboard.kpi.users', 'users'),
      icon: Users,
      trend: translate('dashboard.kpi.selectedPeriod', 'Selected period'),
      color: 'border-amber-500',
      definition: activeUserDefinition
    },
    {
      key: 'totalRevenue',
      title: translate('dashboard.kpi.revenue', 'Revenue'),
      value: formatDashboardRevenue(stats.totalRevenue),
      unit: '',
      icon: DollarSign,
      trend: stats.revenueGrowthRate !== undefined
        ? translate('dashboard.kpi.revenueGrowth', 'Revenue change {{rate}}', { rate: formatSignedPercent(stats.revenueGrowthRate) })
        : periodLabel,
      trendPositive: Number(stats.revenueGrowthRate || 0) >= 0,
      color: 'border-emerald-500',
      definition: revenueDefinition,
      fullValue: `Rp ${Number(stats.totalRevenue || 0).toLocaleString('id-ID')}`
    },
    {
      key: 'activeOffline',
      title: translate('dashboard.kpi.activeOffline', 'Online / Offline'),
      value: `${stats.activeUnits} / ${Math.max(0, stats.totalUnits - stats.activeUnits)}`,
      unit: '',
      icon: Activity,
      trend: translate('dashboard.kpi.realtime', 'Realtime'),
      color: 'border-purple-500'
    }
  ].filter((kpi) => (
    (kpi.key === 'totalUnits' || kpi.key === 'activeOffline') ? canViewUnits
      : kpi.key === 'activeUsers' ? canViewUsers
        : kpi.key === 'totalRevenue' ? canViewDashboard
          : false
  ));

  const rankingSections = [
    {
      key: 'puskud',
      title: translate('dashboard.performance.sections.puskud', 'Regional Partner RP performance ranking'),
      icon: Building2,
      to: '/rp'
    },
    {
      key: 'kud',
      title: translate('dashboard.performance.sections.kud', 'Partner PA performance ranking'),
      icon: Handshake,
      to: '/pa'
    },
    {
      key: 'steward',
      title: translate('dashboard.performance.sections.steward', 'Steward performance ranking'),
      icon: UserCheck,
      to: '/stewards'
    }
  ];

  const rankThemes = [
    { row: 'border-amber-400/60 bg-amber-400/10', badge: 'bg-amber-400 text-slate-950', icon: 'text-amber-300' },
    { row: 'border-slate-300/50 bg-slate-300/10', badge: 'bg-slate-300 text-slate-950', icon: 'text-slate-200' },
    { row: 'border-orange-400/50 bg-orange-400/10', badge: 'bg-orange-400 text-slate-950', icon: 'text-orange-300' }
  ];

  const getInitials = (name = '') => name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '--';

  const formatCurrency = (value) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`;
  const formatNumber = (value) => Number(value || 0).toLocaleString('id-ID');

  return (
    <div className="admin-shell min-h-screen space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <h2 className="admin-text-primary text-3xl font-bold">{translate('dashboard.title', 'Dashboard')}</h2>
            {refreshing && (
              <span className="rounded-full border border-cyan-300/50 bg-cyan-300/10 px-3 py-1 text-xs font-bold text-cyan-600">
                {translate('dashboard.refreshing', 'Updating...')}
              </span>
            )}
          </div>
          <p className="admin-text-secondary text-sm">
            {translate('dashboard.welcome', 'Welcome back, admin')}
            <span className="mx-2">|</span>
            {translate('dashboard.autoRefresh', 'Auto refresh every 60 seconds')}
            {lastUpdated && (
              <>
                <span className="mx-2">|</span>
                {translate('dashboard.lastUpdated', 'Last updated')}: {lastUpdated.toLocaleTimeString()}
              </>
            )}
          </p>
        </div>

        <div className="admin-soft-panel flex flex-col gap-3 rounded-2xl p-3">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarDays size={18} className="text-cyan-600" />
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setPeriod(option.key)}
                className={`rounded-xl px-3 py-2 text-sm font-bold transition ${
                  period === option.key
                    ? 'admin-primary-button'
                    : 'admin-secondary-button'
                }`}
              >
                {translate(option.labelKey, option.fallback)}
              </button>
            ))}
            <button
              type="button"
              onClick={toggleTheme}
              className="admin-secondary-button inline-flex h-10 w-10 items-center justify-center rounded-xl transition"
              aria-label={isDark ? translate('dashboard.theme.switchToLight', 'Switch to light theme') : translate('dashboard.theme.switchToDark', 'Switch to dark theme')}
              title={isDark ? translate('dashboard.theme.light', 'Light theme') : translate('dashboard.theme.dark', 'Dark theme')}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {period === 'month' && (
              <input
                type="month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                className="admin-input rounded-xl px-3 py-2 text-sm"
                aria-label="Select month"
              />
            )}
            {period === 'quarter' && (
              <select
                value={quarter}
                onChange={(event) => setQuarter(event.target.value)}
                className="admin-input rounded-xl px-3 py-2 text-sm"
                aria-label="Select quarter"
              >
                {getQuarterOptions().map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            )}
            {period === 'year' && (
              <input
                type="number"
                min="2020"
                max="2100"
                value={year}
                onChange={(event) => setYear(event.target.value)}
                className="admin-input w-28 rounded-xl px-3 py-2 text-sm"
                aria-label="Select year"
              />
            )}
            {period === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStart}
                  onChange={(event) => setCustomStart(event.target.value)}
                  className="admin-input rounded-xl px-3 py-2 text-sm"
                  aria-label={translate('dashboard.customStartDate', 'Custom start date')}
                />
                <span className="admin-text-muted text-sm">{translate('dashboard.customRangeSeparator', 'to')}</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(event) => setCustomEnd(event.target.value)}
                  className="admin-input rounded-xl px-3 py-2 text-sm"
                  aria-label={translate('dashboard.customEndDate', 'Custom end date')}
                />
              </>
            )}
            <span className="admin-text-muted text-sm">
              {periodCaption}: {periodLabel}
            </span>
          </div>
        </div>
      </header>

      {error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-600">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-semibold">{translate('dashboard.errorTitle', 'Data loading issue')}</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center">
          <Loader2 size={40} className="mx-auto animate-spin text-cyan-500" />
          <p className="admin-text-muted mt-4">{translate('dashboard.loading', 'Loading dashboard data...')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {kpis.map((kpi) => {
              const KpiIcon = kpi.icon;
              const definitionId = `dashboard-definition-${kpi.key}`;
              return (
                <section key={kpi.key} className={`admin-card relative overflow-visible rounded-2xl border-b-4 p-5 ${kpi.color}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="relative flex items-center gap-1">
                        <p className="admin-text-secondary text-sm font-semibold">{kpi.title}</p>
                        {kpi.definition && (
                          <>
                            <button
                              type="button"
                              data-definition-trigger
                              aria-label={translate('dashboard.definitions.open', 'Show metric definition')}
                              aria-expanded={openDefinition === kpi.key}
                              aria-controls={definitionId}
                              onClick={() => setOpenDefinition((current) => (current === kpi.key ? null : kpi.key))}
                              className="admin-secondary-button inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl p-0 transition"
                            >
                              <Info size={15} />
                            </button>
                            {openDefinition === kpi.key && (
                              <div
                                id={definitionId}
                                data-definition-popover
                                role="dialog"
                                aria-label={kpi.title}
                                className="admin-card absolute left-0 top-11 z-30 w-72 max-w-[80vw] rounded-xl p-4 text-sm shadow-2xl"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <p className="admin-text-primary leading-relaxed">{kpi.definition}</p>
                                  <button
                                    type="button"
                                    onClick={() => setOpenDefinition(null)}
                                    className="admin-secondary-button inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg p-0"
                                    aria-label={translate('dashboard.definitions.close', 'Close metric definition')}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <h3 className="admin-text-primary mt-3 break-words text-3xl font-black tabular-nums" title={kpi.fullValue || String(kpi.value)}>
                        {kpi.value} <small className="text-sm font-normal opacity-70">{kpi.unit}</small>
                      </h3>
                      <div className="mt-3 flex items-center gap-1 text-xs font-bold">
                        <TrendingUp size={14} className={kpi.trendPositive !== false ? 'text-emerald-500' : 'text-rose-500'} />
                        <span className={kpi.trendPositive !== false ? 'text-emerald-600' : 'text-rose-600'}>
                          {kpi.trend}
                        </span>
                      </div>
                    </div>
                    <div className="rounded-2xl bg-cyan-400/10 p-3 text-cyan-600">
                      <KpiIcon size={24} />
                    </div>
                  </div>
                </section>
              );
            })}
          </div>

          {canViewDashboard && <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            {rankingSections.map((section) => {
              const SectionIcon = section.icon;
              const items = (rankings[section.key] || []).slice(0, 10);

              return (
                <section key={section.key} className="admin-card min-w-0 rounded-2xl p-5">
                  <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="admin-muted-panel flex h-11 w-11 items-center justify-center rounded-xl text-cyan-600">
                        <SectionIcon size={19} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="admin-text-primary font-bold leading-tight">{section.title}</h3>
                        <p className="admin-text-muted text-xs">TOP 10 | {periodLabel}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 sm:flex-shrink-0 sm:justify-end">
                      <Link
                        to={section.to}
                        className="admin-secondary-button inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold transition"
                      >
                        {translate('dashboard.performance.viewAll', 'View all')}
                        <ChevronRight size={13} />
                      </Link>
                      <Trophy size={18} className="text-amber-500" />
                    </div>
                  </div>

                  <div className="max-h-[720px] space-y-3 overflow-y-auto pr-1">
                    {items.length > 0 ? items.map((rp, index) => {
                      const rank = rp.rank || index + 1;
                      const theme = rankThemes[index] || { row: 'border-cyan-400/30 bg-cyan-400/5', badge: 'bg-cyan-500 text-white', icon: 'text-cyan-500' };
                      const goal = Math.min(100, Math.max(8, Number(rp.goal || 0)));

                      return (
                        <div key={`${section.key}-${rp.id || rank}`} className={`rounded-xl border p-3 ${theme.row}`}>
                          <div className="flex min-w-0 items-start gap-3">
                            <div className="flex flex-shrink-0 flex-col items-center gap-2">
                              <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${theme.badge}`}>
                                {rank}
                              </span>
                              <Medal size={15} className={theme.icon} />
                            </div>
                            <div className="admin-muted-panel flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold">
                              {getInitials(rp.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="admin-text-primary break-words font-bold leading-tight">{rp.name}</p>
                              <div className="admin-text-muted mt-1 flex items-center justify-between gap-3 text-xs">
                                <span className="truncate">{translate('dashboard.performance.units', '{{count}} units', { count: rp.units || 0 })}</span>
                                <span className="flex-shrink-0">{formatNumber(rp.volume)} L</span>
                              </div>
                              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-300/50">
                                <div className="h-full bg-cyan-400 transition-all duration-1000" style={{ width: `${goal}%` }} />
                              </div>
                              <div className="mt-2">
                                <p className="admin-text-muted text-xs">{translate('dashboard.performance.revenue', 'Revenue')}</p>
                                <p className="admin-text-primary break-words text-sm font-bold">{formatCurrency(rp.revenue)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }) : (
                      <p className="admin-text-muted rounded-xl border border-dashed border-slate-300/60 py-8 text-center text-sm">
                        {translate('dashboard.performance.empty', 'No performance data for this period.')}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>}
        </>
      )}
    </div>
  );
}
